import React from 'react';
import { Table, Tag, Avatar, Button, Space, Badge, Popconfirm } from 'antd';
import { EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';

const EmployeesTable = ({
  selectedRowKeys,
  onSelectChange,
  loading,
  data = [],
  total = 0,
  onDelete,
  onView,
  onEdit,
  canEditRecord,
  canDeleteRecord,
}) => {

  const columns = [
    {
      title: 'شناسه کارمند',
      dataIndex: 'employee_id',
      key: 'employee_id',
      width: 110,
      style: { fontFamily: 'CustomFont' },
    },
    {
      title: 'نام',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      render: (text, record) => (
        <div className="flex items-center gap-3">
          <Avatar
            size={40}
            src={record.avatar}
            style={{ backgroundColor: '#1890ff' }}
          >
            {text.charAt(0)}
          </Avatar>
          <div>
            <div
              className="font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer transition-colors"
              style={{ fontFamily: 'CustomFont' }}
              onClick={() => onView?.(record.id)}
            >
              {text}
            </div>
            {record.position && (
              <div className="text-xs text-gray-500 dark:text-gray-400" style={{ fontFamily: 'CustomFont' }}>
                {record.position}
              </div>
            )}
            <div className="flex gap-2 mt-1">
              {record.isNew && (
                <Tag color="blue" style={{ fontFamily: 'CustomFont', fontSize: '11px' }}>
                  استخدام جدید
                </Tag>
              )}
              {record.isCurrentUser && (
                <Tag color="default" style={{ fontFamily: 'CustomFont', fontSize: '11px' }}>
                  این شما هستید
                </Tag>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'ایمیل / تماس',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      render: (email, record) => (
        <div className="flex flex-col gap-0.5" style={{ fontFamily: 'CustomFont' }}>
          <span className="text-gray-800 dark:text-gray-200">{email || '--'}</span>
          {record.mobile && (
            <span className="text-xs text-gray-500 dark:text-gray-400">{record.mobile}</span>
          )}
        </div>
      ),
    },
    {
      title: 'نقش کاربر',
      dataIndex: 'role',
      key: 'role',
      width: 130,
      render: (text) => (
        <div className="flex items-center gap-2">
          <span className="text-gray-600 dark:text-gray-400" style={{ fontFamily: 'CustomFont' }}>
            {text}
          </span>
        </div>
      ),
    },
    {
      title: 'گزارش دهنده به',
      dataIndex: 'reportsTo',
      key: 'reportsTo',
      width: 140,
      render: (text) => (
        <span className="text-gray-600 dark:text-gray-400" style={{ fontFamily: 'CustomFont' }}>
          {text || '--'}
        </span>
      ),
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status) => (
        <Badge
          status={status === 'active' ? 'success' : 'default'}
          text={
            <span style={{ fontFamily: 'CustomFont' }}>
              {status === 'active' ? 'فعال' : 'غیرفعال'}
            </span>
          }
        />
      ),
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => onView?.(record.id)}
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
            style={{ fontFamily: 'CustomFont' }}
            title="مشاهده"
          />
          {onEdit && (!canEditRecord || canEditRecord(record)) ? (
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => onEdit(record.id)}
              className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
              style={{ fontFamily: 'CustomFont' }}
              title="ویرایش"
            />
          ) : null}
          {onDelete && (!canDeleteRecord || canDeleteRecord(record)) ? (
            <Popconfirm
              title="حذف کارمند"
              description="آیا از حذف این کارمند اطمینان دارید؟"
              onConfirm={() => onDelete(record.id)}
              okText="بله، حذف کن"
              cancelText="لغو"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                className="hover:bg-red-50 dark:hover:bg-red-900/20"
                style={{ fontFamily: 'CustomFont' }}
                title="حذف"
              />
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm" style={{ fontFamily: 'CustomFont' }}>
      <Table
        columns={columns}
        dataSource={data}
        rowSelection={rowSelection}
        loading={loading}
        rowKey="id"
        pagination={{
          total: total || data.length,
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (t, range) => (
            <span style={{ fontFamily: 'CustomFont' }}>
              نمایش {range[0]} تا {range[1]} از {t} ردیف
            </span>
          ),
          pageSizeOptions: ['10', '20', '40', '100'],
        }}
        className="custom-table"
      />
      <style>{`
        .custom-table,
        .custom-table * {
          font-family: 'CustomFont', 'Vazir', 'Tahoma', sans-serif !important;
        }
        .custom-table .ant-table,
        .custom-table .ant-table-thead > tr > th,
        .custom-table .ant-table-tbody > tr > td,
        .custom-table .ant-pagination,
        .custom-table .ant-pagination-item,
        .custom-table .ant-pagination-item-link,
        .custom-table .ant-select-selector,
        .custom-table .ant-select-selection-item {
          font-family: 'CustomFont', 'Vazir', 'Tahoma', sans-serif !important;
        }
      `}</style>
    </div>
  );
};

export default EmployeesTable;

